// src/components/ReviewConfirmStep.jsx
import React from "react";
import { CheckCircle2, FileText, ArrowLeft, Check } from "lucide-react";
import { consentVersion as defaultConsentVersion } from "./data/mockData";

export default function ReviewConfirmStep({
  purposes,
  declared,
  onDeclare,
  onBack,
  onSubmit,
  emails = [],
  recipientNames = {},
  recipientPhones = {},
  recipientDobs = {},
  consentVersion = defaultConsentVersion, // <-- ab parent se dynamic version aa sakta hai
}) {
  const mandatoryCount = purposes.mandatory.items.filter((i) => i.checked).length;
  const optionalCount = purposes.optional.items.filter((i) => i.checked).length;
  const thirdPartyCount = purposes.thirdParty.items.filter((i) => i.checked).length;

  const summaryLines = [
    `You have selected ${mandatoryCount} mandatory purpose${mandatoryCount === 1 ? "" : "s"}`,
    `You have selected ${optionalCount} optional purpose${optionalCount === 1 ? "" : "s"}`,
    `You have selected ${thirdPartyCount} third-party sharing option${thirdPartyCount === 1 ? "" : "s"}`,
    "Data will be retained for 7 years as per policy",
    "You can withdraw optional consents anytime",
  ];

  return (
    <section
      style={{
        background: "#fff",
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--gray-200)",
        boxShadow: "var(--shadow-sm)",
        padding: "20px 28px 28px",
        width: "100%",
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "var(--blue-700)",
          background: "var(--blue-50)",
          padding: "3px 9px",
          borderRadius: 5,
          letterSpacing: 0.3,
        }}
      >
        STEP 3 OF 4
      </span>
      <h2 style={{ fontSize: 21, fontWeight: 700, color: "var(--gray-900)", margin: "10px 0 4px" }}>
        Review &amp; Confirm
      </h2>
      <p style={{ fontSize: 13.5, color: "var(--gray-500)", margin: "0 0 20px" }}>
        Review your selections before providing consent
      </p>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {/* Your Selections */}
        <div
          style={{
            flex: "1 1 220px",
            border: "1px solid var(--gray-200)",
            borderRadius: "var(--radius-md)",
            padding: 18,
          }}
        >
          <h4 style={{ fontSize: 13.5, fontWeight: 700, color: "var(--gray-900)", margin: "0 0 16px" }}>
            Your Selections
          </h4>
          {[
            ["Mandatory Purposes", mandatoryCount],
            ["Optional Purposes", optionalCount],
            ["Third-Party Sharing", thirdPartyCount],
          ].map(([label, count]) => (
            <div
              key={label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, color: "var(--gray-600)" }}>{label}</span>
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "var(--green-700)",
                  background: "var(--green-100)",
                  padding: "3px 10px",
                  borderRadius: 6,
                }}
              >
                {count} Selected
              </span>
            </div>
          ))}
        </div>

        {/* Recipients */}
        <div
          style={{
            flex: "1 1 220px",
            border: "1px solid var(--gray-200)",
            borderRadius: "var(--radius-md)",
            padding: 18,
            minWidth: 220,
          }}
        >
          <h4 style={{ fontSize: 13.5, fontWeight: 700, color: "var(--gray-900)", margin: "0 0 12px" }}>
            Recipients
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {emails.length === 0 && <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>No recipients added.</div>}
            {emails.map((e) => (
              <div key={e} style={{ fontSize: 13, color: 'var(--gray-700)' }}>
                <div style={{ fontWeight: 700 }}>{recipientNames?.[e] || ''}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{e}</div>
                {recipientPhones?.[e] && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{recipientPhones[e]}</div>}
                {recipientDobs?.[e] && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{recipientDobs[e]}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div
          style={{
            flex: "1.4 1 280px",
            border: "1px solid var(--gray-200)",
            borderRadius: "var(--radius-md)",
            padding: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <h4 style={{ fontSize: 13.5, fontWeight: 700, color: "var(--gray-900)", margin: 0 }}>
              Summary
            </h4>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {summaryLines.map((line) => (
              <div key={line} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <CheckCircle2
                  size={15}
                  style={{ color: "var(--green-600)", flexShrink: 0, marginTop: 1 }}
                />
                <span style={{ fontSize: 13, color: "var(--gray-700)" }}>{line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Declaration */}
        <div
          style={{
            flex: "1 1 220px",
            background: "var(--green-50)",
            border: "1px solid var(--green-200)",
            borderRadius: "var(--radius-md)",
            padding: 18,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <FileText size={16} style={{ color: "var(--green-700)" }} />
            <h4 style={{ fontSize: 13.5, fontWeight: 700, color: "var(--gray-900)", margin: 0 }}>
              Your Declaration
            </h4>
          </div>
          <p style={{ fontSize: 13, color: "var(--gray-700)", lineHeight: 1.5, margin: "0 0 14px" }}>
            I confirm that I have read, understood, and voluntarily provide my consent
            for the selected purposes.
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 13,
              color: "var(--gray-700)",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={declared}
              onChange={(e) => onDeclare(e.target.checked)}
              style={{
                width: 16,
                height: 16,
                marginTop: 1,
                accentColor: "var(--blue-700)",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
            I agree to the above declaration.
          </label>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 22,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#fff",
            border: "1px solid var(--gray-200)",
            color: "var(--gray-700)",
            borderRadius: 8,
            padding: "10px 16px",
            fontSize: 13.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={15} />
          Back to Purpose Selection
        </button>

        <button
          type="button"
          disabled={!declared}
          onClick={onSubmit}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: declared ? "var(--blue-700)" : "#94a3b8",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            fontSize: 13.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Provide Consent
          <Check size={15} />
        </button>
      </div>
    </section>
  );
}